import React, { useRef, useState, useMemo, useCallback, useEffect } from "react";
import { useSelector } from "react-redux";
import { MenuItem, Divider } from "@mui/material";
import styles from "./DocumentDialog.module.css";
import {
	CustomDialog,
	CustomTextField,
	icon,
	IconComponent,
	SearchListContent,
} from "../../../../../../../../../../common/components";
import { isSegFeatureEnabled, SegFlags, translate } from "../../../../../../../../../../common/providers";
import { ApiService, DocumentOutService } from "../../../../../../../../../../api";
import FormView from "../../../../../../../../../deliverables-list/components/deliverable-side-panel/FormView";
import { debounce } from "../../../../../../../../../../common/utils";

const initialState = (document) => {
	if (document) {
		return {
			name: document.name,
			ref: document.ref,
			ownerId: (document.owner && document.owner.id) || "",
			format: document.format,
			type: document.type,
			thematics: (document?.thematic && document.thematic.map((c) => c.id)) || [],
			phaseId: (document.phase && document.phase.id) || "",
		};
	}
	return { name: "", ref: "", format: "", type: "", thematics: [], phaseId: "", ownerId: "" };
};
const initialErrorState = { name: false, ref: false, type: false, format: false, owner: false };

const DocumentDialog = ({
	action,
	document = null,
	fbs = [],
	formats = [],
	onClose,
	onExpand,
	open,
	owners = [],
	phases = [],
	types = [],
}) => {
	const projectId = useSelector(({ context }) => context?.project?.id);
	const [errors, setErrors] = useState(initialErrorState);
	const [payload, setPayload] = useState(initialState(document));
	const [isCategoriesOpen, setIsCategoriesOpen] = useState(false);
	const [isRefDuplicate, setIsRefDuplicate] = useState(false);
	const [isNameDuplicate, setIsNameDuplicate] = useState(false);
	const isMountedRef = useRef(true);
	const duplicateCancelSrcRef = useRef(null);
	const isFormValid =
		payload.name?.length > 2 &&
		payload.ref &&
		payload.ownerId &&
		payload.type &&
		payload.format &&
		payload.phaseId &&
		!isRefDuplicate &&
		!isNameDuplicate;
	useEffect(() => {
		isMountedRef.current = true;
		return () => {
			isMountedRef.current = false;
		};
	}, []);
	useEffect(() => {
		if (!isMountedRef.current) {
			return;
		}
		setPayload(initialState(document));
		setIsCategoriesOpen(false);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [document?.id]);
	useEffect(() => {
		if (!isMountedRef.current || !document) {
			return;
		}
		setPayload((prev) => {
			const updated = { ...prev };
			let needsUpdate = false;
			if (owners.length > 0) {
				const currentOwnerExists = updated.ownerId && owners.find((o) => o.id === updated.ownerId);
				if (!currentOwnerExists) {
					if (document.owner?.id) {
						const matchingOwner = owners.find((o) => o.id === document.owner.id);
						if (matchingOwner?.id && matchingOwner.id !== updated.ownerId) {
							updated.ownerId = matchingOwner.id;
							needsUpdate = true;
						}
					} else if (document.ownerName) {
						const matchingOwner = owners.find(
							(o) => o.displayName === document.ownerName || o.name === document.ownerName
						);
						if (matchingOwner?.id && matchingOwner.id !== updated.ownerId) {
							updated.ownerId = matchingOwner.id;
							needsUpdate = true;
						}
					} else if (document.ownerId && !updated.ownerId) {
						const matchingOwner = owners.find((o) => o.id === document.ownerId);
						if (matchingOwner?.id) {
							updated.ownerId = matchingOwner.id;
							needsUpdate = true;
						}
					}
				}
			}
			if (phases.length > 0) {
				const currentPhaseExists = updated.phaseId && phases.find((p) => p.id === updated.phaseId);

				if (!currentPhaseExists) {
					if (document.phase?.id) {
						const matchingPhase = phases.find((p) => p.id === document.phase.id);
						if (matchingPhase?.id && matchingPhase.id !== updated.phaseId) {
							updated.phaseId = matchingPhase.id;
							needsUpdate = true;
						}
					} else if (document.phaseName) {
						const matchingPhase = phases.find((p) => p.name === document.phaseName);
						if (matchingPhase?.id && matchingPhase.id !== updated.phaseId) {
							updated.phaseId = matchingPhase.id;
							needsUpdate = true;
						}
					} else if (document.phaseId && !updated.phaseId) {
						const matchingPhase = phases.find((p) => p.id === document.phaseId);
						if (matchingPhase?.id) {
							updated.phaseId = matchingPhase.id;
							needsUpdate = true;
						}
					}
				}
			}

			return needsUpdate ? updated : prev;
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [owners.length, phases.length, document?.owner?.id, document?.phase?.id]);

	useEffect(
		() => () => {
			if (duplicateCancelSrcRef.current && duplicateCancelSrcRef.current.cancel) {
				duplicateCancelSrcRef.current.cancel();
			}
		},
		[]
	);

	const debouncedCheckDuplicates = useMemo(
		() =>
			debounce(async ({ ref, name }) => {
				if (!projectId) {
					return;
				}
				const refTrimmed = typeof ref === "string" ? ref.trim() : "";
				const nameTrimmed = typeof name === "string" ? name.trim() : "";
				if (duplicateCancelSrcRef.current && duplicateCancelSrcRef.current.cancel) {
					duplicateCancelSrcRef.current.cancel("canceled by new request");
				}
				const cancelSource = ApiService.getCancelTokenSource();
				duplicateCancelSrcRef.current = cancelSource;
				try {
					if (refTrimmed) {
						try {
							const refResponse = await DocumentOutService.existsByProjectAndRef(
								{ projectId },
								{ ref: refTrimmed },
								cancelSource.token
							);
							const refExists = refResponse.value === true || refResponse.value === "true";
							setIsRefDuplicate(Boolean(refExists));
						} catch (err) {
							console.error("Error checking ref duplicate:", err);
						}
					} else {
						setIsRefDuplicate(false);
					}

					if (nameTrimmed && nameTrimmed.length >= 3) {
						try {
							const nameResponse = await DocumentOutService.existsByProjectAndName(
								{ projectId },
								{ name: nameTrimmed },
								cancelSource.token
							);
							const nameExists = nameResponse.value === true || nameResponse.value === "true";
							setIsNameDuplicate(Boolean(nameExists));
						} catch (err) {
							console.error("Error checking name duplicate:", err);
						}
					} else {
						setIsNameDuplicate(false);
					}
				} catch (err) {
					console.error("Error checking duplicates:", err);
				} finally {
					duplicateCancelSrcRef.current = null;
				}
			}, 300),
		[projectId]
	);

	const checkDuplicates = useCallback(
		({ ref, name }) => {
			if (!projectId) {
				return;
			}

			debouncedCheckDuplicates({ ref, name });
		},
		[projectId, debouncedCheckDuplicates]
	);
	const changeMandatoryFields = useCallback(
		(content, field) => {
			if (!content) {
				setErrors((prev) => ({ ...prev, [field]: true }));
			} else if (errors[field]) {
				setErrors((prev) => ({ ...prev, [field]: false }));
			}
			setPayload((prev) => ({ ...prev, [field]: content }));
			if (field === "ref") {
				checkDuplicates({ ref: content, name: payload.name });
			}
		},
		[errors, checkDuplicates, payload.name]
	);

	const changeName = useCallback(
		(name) => {
			if (!name || name.length < 3) {
				setErrors((prev) => ({ ...prev, name: true }));
			} else if (errors.name) {
				setErrors((prev) => ({ ...prev, name: false }));
			}
			setPayload((prev) => ({ ...prev, name }));
			checkDuplicates({ ref: payload.ref, name });
		},
		[errors.name, checkDuplicates, payload.ref]
	);

	const changeNullableFields = useCallback((content, field) => {
		setPayload((prev) => ({ ...prev, [field]: content }));
	}, []);

	const handleCheckCategory = useCallback((checkedElements) => {
		setPayload((prev) => ({ ...prev, thematics: checkedElements }));
	}, []);
	const handleToggleCategories = useCallback(() => {
		setIsCategoriesOpen((prev) => !prev);
	}, []);

	const reset = () => {
		setErrors(initialErrorState);
		setPayload(initialState(document));
		setIsCategoriesOpen(false);
	};

	const handleSubmit = () => {
		const errs = {
			name: !(payload.name && payload.name.length > 2),
			ref: !payload.ref,
			owner: !payload.ownerId,
			type: !payload.type,
			format: !payload.format,
			phaseId: !payload.phaseId,
		};
		if (!Object.keys(errs).some((key) => errs[key])) {
			action(payload);
			onClose();
		}

		setErrors(errs);
	};

	const handleCancel = () => {
		reset();
		onClose();
	};

	return (
		<CustomDialog
			disableBackdropClick
			displayCloseCross
			isForm
			maxWidth="lg"
			open={open}
			submitDisabled={!isFormValid}
			title={
				document
					? translate("common:deliverables.document-dialog.doc-update")
					: translate("common:deliverables.document-dialog.doc-create")
			}
			onClose={handleCancel}
			onExited={reset}
			onSubmit={handleSubmit}
		>
			<FormView
				document={document}
				errors={errors}
				fbs={fbs}
				formats={formats}
				isCategoriesOpen={isCategoriesOpen}
				isNameDuplicate={isNameDuplicate}
				isRefDuplicate={isRefDuplicate}
				mode="add"
				owners={owners}
				payload={payload}
				phases={phases}
				types={types}
				onChangeMandatoryFields={changeMandatoryFields}
				onChangeName={changeName}
				onChangeNullableFields={changeNullableFields}
				onCheckCategory={handleCheckCategory}
				onExpand={onExpand}
				onToggleCategories={handleToggleCategories}
			/>
			{isSegFeatureEnabled(SegFlags.DISABLED) && (
				<>
					<div className={styles["margin-bottom"]}>
						<CustomTextField
							fullWidth
							required
							error={errors.name}
							helperText={errors.name && translate("common:error.required-with-min-data", { min: "3" })}
							label={translate("common:deliverables.document-dialog.doc-name")}
							size="small"
							value={payload.name || (document && document.name) || ""}
							variant="outlined"
							onChange={(e) => changeName(e.target.value)}
						/>
						<CustomTextField
							fullWidth
							required
							select
							className={styles["dialog__select-field"]}
							error={errors.format}
							helperText={errors.format && translate("common:error.required")}
							label={translate("common:deliverables.document-dialog.format")}
							size="small"
							value={(payload && payload.format) || (document && document.format) || ""}
							variant="outlined"
							onChange={(e) => changeMandatoryFields(e.target.value, "format")}
						>
							{formats &&
								formats.map((format) => (
									<MenuItem key={format} value={format}>
										{format}
									</MenuItem>
								))}
						</CustomTextField>
						<CustomTextField
							fullWidth
							required
							select
							className={styles["dialog__select-field"]}
							error={errors.type}
							helperText={errors.type && translate("common:error.required")}
							label={translate("common:deliverables.document-dialog.type")}
							size="small"
							value={(payload && payload.type) || (document && document.type) || ""}
							variant="outlined"
							onChange={(e) => changeMandatoryFields(e.target.value, "type")}
						>
							{types &&
								types.map((type) => (
									<MenuItem key={type} value={type}>
										{type}
									</MenuItem>
								))}
						</CustomTextField>
					</div>
					<div className={styles["margin-bottom"]} style={{ display: "flex" }}>
						<CustomTextField
							fullWidth
							required
							error={errors.ref}
							helperText={errors.ref && translate("common:error.required")}
							label={translate("common:deliverables.document-dialog.ref-id")}
							size="small"
							value={payload.ref || (document && document.ref) || ""}
							variant="outlined"
							onChange={(e) => changeMandatoryFields(e.target.value, "ref")}
						/>
						<span style={{ margin: "0 8px" }} />
						<CustomTextField
							fullWidth
							required
							select
							error={errors.owner}
							helperText={errors.owner && translate("common:error.required")}
							label={translate("common:deliverables.document-dialog.owner")}
							size="small"
							value={payload.ownerId || (document && document.owner && document.owner.id) || ""}
							variant="outlined"
							onChange={(e) => changeMandatoryFields(e.target.value, "ownerId")}
						>
							{owners.map((owner) => (
								<MenuItem key={owner.id} value={owner.id}>
									{owner.displayName}
								</MenuItem>
							))}
						</CustomTextField>
					</div>
					<CustomTextField
						fullWidth
						required
						select
						className={styles["margin-bottom"]}
						error={errors.phaseId}
						helperText={errors.phaseId && translate("common:error.required")}
						label={translate("common:deliverables.document-dialog.phase-selection")}
						size="small"
						value={payload.phaseId || (document && document.phase && document.phase.id) || ""}
						variant="outlined"
						onChange={(e) => changeNullableFields(e.target.value, "phaseId")}
					>
						{phases.map((phase) => (
							<MenuItem key={phase.id} disabled={Boolean(phase.locked || !phase.active)} value={phase.id}>
								<div
									className={styles.phaseItem}
									data-phase-locked={Boolean(phase.locked || !phase.active)}
								>
									<span>{phase.name}</span>
									{(phase.locked || !phase.active) && (
										<IconComponent color="var(--grey-500)" icon={icon.faLock} />
									)}
								</div>
							</MenuItem>
						))}
					</CustomTextField>
					<Divider className={styles["margin-bottom"]} />
					<div className={styles["dialog__content-title"]}>
						{translate("common:deliverables.document-dialog.fbs-selection")}
					</div>
					<SearchListContent
						isTree
						initChecked={(document?.thematics && document.thematics.map((c) => c.id)) || []}
						rowKey="id"
						rowLabelKey="name"
						rows={fbs}
						rowTooltipKey="name"
						subRowCounter="childrenCount"
						subRowKey="subThematic"
						onClickRow={handleCheckCategory}
						onExpand={onExpand}
					/>
				</>
			)}
		</CustomDialog>
	);
};
export default DocumentDialog;
